import { B2BClient, B2BClientOrderRelation } from '../clients/types';
import { B2BOrder } from '../orders/types';
import {
  AgentApiClient,
  AgentApiMeta,
  AgentApiOrder,
  AgentApiTourist,
  AgentApiLead,
} from './types';

const formatDate = (value?: string, includeTime = false) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: includeTime ? undefined : 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

export const getAgentClientFullName = (client: AgentApiClient) =>
  [client.surname, client.name, client.patronymic].filter(Boolean).join(' ') ||
  'Без имени';

const serializeTourist = (tourist: AgentApiTourist) => ({
  id: String(tourist.id),
  name: tourist.name || '',
  surname: tourist.surname || '',
  patronymic: tourist.patronymic || '',
  fullName:
    [tourist.surname, tourist.name, tourist.patronymic]
      .filter(Boolean)
      .join(' ') || 'Без имени',
  gender: tourist.gender,
  birthday: tourist.birthday || '',
  birthDate: formatDate(tourist.birthday),
  nationalityId: tourist.nationality_id || tourist.nationality?.id || null,
  nationalityName: tourist.nationality?.name_ru || 'Не указано',
  documentType: tourist.doc_type || null,
  passportPrefix: tourist.passport_prefix || '',
  passportNumber: tourist.passport_number || '',
  passport:
    [tourist.passport_prefix, tourist.passport_number]
      .filter(Boolean)
      .join(' ') || '—',
  passportIssuedOn: tourist.passport_issued_on || '',
  passportExpiresOn: tourist.passport_expires_on || '',
  passportIssuer: tourist.passport_issuer || '',
  phone: tourist.phone || '',
  email: tourist.email || '',
  visaService: tourist.visa_service || 'no_visa',
});

export const serializeAgentOrder = (order: AgentApiOrder): B2BOrder => {
  const isPaid = order.price > 0 && order.paid_amount >= order.price;

  return {
    id: String(order.id),
    clientId: String(order.client.id),
    clientName: order.client.name,
    clientSurname: order.client.surname,
    touristName: getAgentClientFullName(order.client),
    clientPhone: order.client.phone,
    touristsCount: order.adults + order.kids,
    tourists: (order.tourists || []).map(serializeTourist),
    hotelId: order.hotel ? String(order.hotel.id) : null,
    hotelName: order.hotel?.name || 'Отель не указан',
    dates: formatDate(order.start_date),
    nights: order.nights,
    room: order.room_type || 'Номер не указан',
    amount: order.price,
    paidAmount: order.paid_amount,
    status: order.state,
    createdAt: formatDate(order.created_at, true),
    paymentStatus: isPaid ? 'online_paid' : 'unpaid',
    events: (order.logs || []).map((log) => ({
      id: String(log.id),
      title: log.message,
      time: formatDate(log.created_at, true),
    })),
  };
};

export const serializeAgentClient = (
  client: AgentApiClient,
  orders: B2BClientOrderRelation[] = []
): B2BClient => ({
  id: String(client.id),
  name: client.name,
  surname: client.surname,
  patronymic: client.patronymic,
  fullName: getAgentClientFullName(client),
  phone: client.phone,
  email: client.email,
  unconfirmedEmail: client.unconfirmed_email || '',
  unconfirmedPhone: client.unconfirmed_phone || '',
  createdAt: formatDate(client.created_at),
  orders,
});

export const serializeClientOrderRelation = (
  order: AgentApiOrder
): B2BClientOrderRelation => ({
  id: String(order.id),
  destination: formatDate(order.start_date),
  hotelName: order.hotel?.name || 'Отель не указан',
  status: order.state,
});

export const serializeAgentMeta = (meta: AgentApiMeta) => ({
  page: meta.page,
  perPage: meta.per_page,
  total: meta.total,
});

export const serializeAgentLead = (lead: AgentApiLead) => ({
  id: String(lead.id),
  status: lead.status,
  closeReason: lead.close_reason,
  source: lead.source,
  name: lead.name || '',
  phone: lead.phone || '',
  email: lead.email || '',
  comment: lead.comment || '',
  createdAt: formatDate(lead.created_at, true),
  partner: lead.partner,
  package: lead.package
    ? {
        ...lead.package,
        startDate: formatDate(lead.package.start_date),
      }
    : null,
  client: lead.client ? serializeAgentClient(lead.client) : null,
  agencyUser: lead.agency_user,
  orders: (lead.orders || []).map((order) => ({
    id: String(order.id),
    state: order.state,
    createdAt: formatDate(order.created_at, true),
  })),
});
